import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { FiCompareHtmlService } from '@fi-app/utilities/compare-html/compare-html.service';
import { LinkToTypesService } from '@fi-app/shared/link-to/link-to-types.service';
import { IMediaBankFileReference } from '@fi-shared/media-bank';
import { MediaViewerService } from '@fi-shared/media-viewer/media-viewer.service';
import { FiMediaStorageService } from '@fi-shared/media-bank/fi-media-storage.service';
import { FiVimeoService } from '@fi-shared/media-bank/fi-vimeo/fi-vimeo.service';
import { FiBrowserEventsService } from '@fi-shared/fi-shared-services/fi-browser-events.service';
import { debounce, takeUntil } from 'rxjs/operators';
import { interval, Subject } from 'rxjs';
import { FiTextItemHelperService } from '@fi-shared/fi-text-item-helper/fi-text-item-helper.service';
import { FiApplicationLanguageType } from '@fi-shared/fi-content-language-shared/fi-application-language-type';

@Component({
    selector: 'fi-document-layout-item-text-view',
    templateUrl: './layout-item-text-view.component.html',
    styleUrls: ['./layout-item-text-view.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: FiDocumentLayoutItemTextViewComponent,
        },
    ],
    host: {
        class: 'fi-document-layout-item-text-view',
        '(click)': 'onClick($event)',
    },
    standalone: false,
})
export class FiDocumentLayoutItemTextViewComponent implements OnInit, OnChanges, OnDestroy {
    @Input() ngModel: string;
    @Input() mediaBankFileReferences: IMediaBankFileReference[];
    @Input() compareWith: FiDocumentLayoutItemTextViewComponent;
    @Input() contentLanguageId: FiApplicationLanguageType;

    @Output() afterRender = new EventEmitter();
    @ViewChild('compareHtml', { static: false }) compareHtml: ElementRef;

    constructor(
        private compareHtmlService: FiCompareHtmlService,
        private fiTextItemHelperService: FiTextItemHelperService,
        private elementRef: ElementRef,
        private linkToTypesService: LinkToTypesService,
        private mediaViewerService: MediaViewerService,
        private mediaStorageService: FiMediaStorageService,
        private vimeoService: FiVimeoService,
        private fiBrowserEventsService: FiBrowserEventsService,
    ) {}

    ngOnInit() {
        this.fiBrowserEventsService
            .getWindowEventSubject$('resize')
            .pipe(
                takeUntil(this.ngOnDestroy$),
                debounce(() => interval(100)),
            )
            .subscribe(() => {
                this.vimeoService.setVimeoPreviewIconsSize(this.elementRef.nativeElement);
            });
    }

    isEmpty() {
        return !(this.ngModel && this.ngModel.length > 0);
    }

    // note: not used ?
    propagateChange = (_: any) => {};

    writeValue(obj: any) {
        this.ngModel = obj;
    }

    registerOnChange(fn: any) {
        this.propagateChange = fn;
    }

    // note: not used ?
    registerOnTouched(fn: any) {}

    afterHtmlUpdate() {
        this.afterRender.emit();
        this._compareHtml();
        const element = this.elementRef.nativeElement.querySelector('.text-content');
        this.fiTextItemHelperService.updateTemplateItemTextImage(element, this.mediaBankFileReferences);
        //this.textItemMediaReferencesService.removeNonExistingMediaReferences(element, this.mediaReferences);
        setTimeout(() => this.vimeoService.setVimeoPreviewIconsSize(this.elementRef.nativeElement), 1);
    }

    ngOnChanges(changes) {
        if (changes.compareWith) this._compareHtml();
    }

    protected _compareHtml() {
        if (!this.compareWith || !this.compareWith.compareHtml || !this.compareHtml) return;

        this.compareHtmlService.compareNodes(
            this.compareWith.compareHtml.nativeElement,
            this.compareHtml.nativeElement,
        );
    }

    onClick($event) {
        this.fiTextItemHelperService.onTextItemClick($event, this.mediaBankFileReferences);
    }

    getDirRtlAttr() {
        return this.fiTextItemHelperService.getDirRtlAttr(this.contentLanguageId);
    }

    getTextAlignRtlAttr() {
        return this.fiTextItemHelperService.getTextAlignRtlAttr(this.contentLanguageId);
    }

    private ngOnDestroy$ = new Subject<void>();
    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
